import React, { useState } from "react";
import axios from "axios";
import { CheckCircle2, AlertCircle, Loader2, Send } from "lucide-react";
import { useLang } from "../lib/LangContext";
import { Reveal } from "./Reveal";
import { TID } from "../lib/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { name: "", phone: "", email: "", service: "", message: "" };

export default function QuoteForm() {
  const { t, lang } = useLang();
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [errorMsg, setErrorMsg] = useState("");

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    if (!form.name || !form.phone || !form.email || !form.service || !form.message) {
      setStatus("error");
      setErrorMsg(t.quote.error);
      return;
    }
    setStatus("sending");
    try {
      const res = await axios.post(`${API}/quote`, { ...form, language: lang });
      if (res?.data?.status === "success") {
        setStatus("success");
        setForm(EMPTY);
      } else {
        setStatus("error");
        setErrorMsg(t.quote.error);
      }
    } catch (err) {
      setStatus("error");
      setErrorMsg(t.quote.error);
    }
  };

  const labelCls = "block text-xs uppercase tracking-[0.2em] font-semibold text-white/70 mb-2";
  const inputCls =
    "w-full bg-transparent border border-white/15 text-white placeholder-white/35 px-4 py-3.5 focus:border-[#D4AF37] focus:outline-none transition-colors text-base font-light";

  const options = t.quote.fields.servicesOptions;

  return (
    <section
      id="quote"
      data-testid={TID.quoteSection}
      className="relative bg-[#0A0A0A] py-24 md:py-32 grain"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <Reveal className="lg:col-span-5">
            <span className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] font-semibold">
              — {t.quote.eyebrow}
            </span>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight mt-4 leading-tight">
              {t.quote.title}
            </h2>
            <p className="mt-6 text-white/60 text-lg leading-relaxed max-w-md">
              {t.quote.description}
            </p>
            <div className="mt-10 hidden lg:block border-t border-white/10 pt-8">
              <p className="text-xs uppercase tracking-[0.3em] text-white/40">Direkt</p>
              <a
                href="tel:+41783215249"
                className="block mt-3 font-display text-3xl text-white hover:text-[#D4AF37] transition-colors"
              >
                +41 78 321 52 49
              </a>
              <a
                href="mailto:info@zemiswiss.ch"
                className="block mt-1 text-white/60 hover:text-white transition-colors"
              >
                info@zemiswiss.ch
              </a>
            </div>
          </Reveal>

          <Reveal delay={120} className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              data-testid={TID.quoteForm}
              className="bg-[#151515] border border-white/10 p-6 sm:p-10"
              noValidate
            >
              {status === "success" && (
                <div
                  data-testid={TID.quoteSuccess}
                  className="mb-6 flex items-start gap-3 p-4 border border-[#D4AF37]/40 bg-[#D4AF37]/10"
                >
                  <CheckCircle2 className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-white leading-relaxed">{t.quote.success}</p>
                </div>
              )}
              {status === "error" && errorMsg && (
                <div
                  data-testid={TID.quoteError}
                  className="mb-6 flex items-start gap-3 p-4 border border-[#E3000F]/50 bg-[#E3000F]/10"
                >
                  <AlertCircle className="w-5 h-5 text-[#E3000F] flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-white leading-relaxed">{errorMsg}</p>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className={labelCls} htmlFor="zs-name">
                    {t.quote.fields.name}*
                  </label>
                  <input
                    id="zs-name"
                    name="name"
                    type="text"
                    autoComplete="name"
                    required
                    value={form.name}
                    onChange={onChange}
                    data-testid={TID.quoteName}
                    className={inputCls}
                    placeholder="Max Muster"
                  />
                </div>
                <div>
                  <label className={labelCls} htmlFor="zs-phone">
                    {t.quote.fields.phone}*
                  </label>
                  <input
                    id="zs-phone"
                    name="phone"
                    type="tel"
                    autoComplete="tel"
                    required
                    value={form.phone}
                    onChange={onChange}
                    data-testid={TID.quotePhone}
                    className={inputCls}
                    placeholder="+41 78 …"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls} htmlFor="zs-email">
                    {t.quote.fields.email}*
                  </label>
                  <input
                    id="zs-email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={form.email}
                    onChange={onChange}
                    data-testid={TID.quoteEmail}
                    className={inputCls}
                    placeholder="name@example.ch"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls} htmlFor="zs-service">
                    {t.quote.fields.service}*
                  </label>
                  <select
                    id="zs-service"
                    name="service"
                    required
                    value={form.service}
                    onChange={onChange}
                    data-testid={TID.quoteService}
                    className={`${inputCls} appearance-none bg-[url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'><path d='M1 1l5 5 5-5' stroke='%23D4AF37' stroke-width='1.5' fill='none' stroke-linecap='round'/></svg>")] bg-no-repeat bg-[right_1rem_center] pr-10`}
                  >
                    <option value="" className="bg-[#151515] text-white">
                      {t.quote.fields.servicePlaceholder}
                    </option>
                    <option value="transport" className="bg-[#151515] text-white">
                      {options.transport}
                    </option>
                    <option value="reinigung" className="bg-[#151515] text-white">
                      {options.reinigung}
                    </option>
                    <option value="beides" className="bg-[#151515] text-white">
                      {options.beides}
                    </option>
                    <option value="andere" className="bg-[#151515] text-white">
                      {options.andere}
                    </option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls} htmlFor="zs-message">
                    {t.quote.fields.message}*
                  </label>
                  <textarea
                    id="zs-message"
                    name="message"
                    rows={5}
                    required
                    value={form.message}
                    onChange={onChange}
                    data-testid={TID.quoteMessage}
                    className={`${inputCls} resize-y min-h-[120px]`}
                    placeholder={t.quote.fields.messagePlaceholder}
                  />
                </div>
              </div>

              <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <p className="text-xs text-white/40 max-w-sm leading-relaxed">{t.quote.privacy}</p>
                <button
                  type="submit"
                  disabled={status === "sending"}
                  data-testid={TID.quoteSubmit}
                  className="group inline-flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#C5A028] disabled:opacity-60 disabled:cursor-not-allowed text-black font-semibold px-8 py-4 transition-all duration-300 min-w-[200px]"
                >
                  {status === "sending" ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>{t.quote.submitting}</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                      <span>{t.quote.submit}</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
